import { FC, ReactNode } from "react";
import {
  Box,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { LocalHospital, Person, PersonOff } from "@mui/icons-material";
import DisqualificationCard from "../custom-icons/DisqualificationCard";
import {
  AdmissionStatus,
  Official,
  Player,
  TeamSquad,
} from "@/models/gameGeneral";
import { gameState, useTeamSides } from "@/services/gameState";
import { useAppDataContext } from "@context/AppContext";
import {
  CenteredHeaderTableCell,
  CenteredTableCell,
} from "../table/TableComponents";
import PaddedDivider from "@components/layout/PaddedDivider";
import SwapHorizAlert from "@components/custom-icons/SwapHorizAlert";
import { getAddressee } from "@/utils/persons";

interface TeamIneligibleProps {
  side: "left" | "right";
  persons: (Player | Official)[];
}

const IneligibleSymbolMap: {
  [key in Exclude<AdmissionStatus, "ADMITTED">]: () => ReactNode;
} = {
  INJURED: () => <LocalHospital sx={{ fontSize: "2.5vw" }} />,
  UNABLE_TO_PLAY: () => <PersonOff sx={{ fontSize: "2.5vw" }} />,
  DISQUALIFIED: () => <DisqualificationCard sx={{ fontSize: "2.5vw" }} />,
  EXCEPTIONALLY_SUBSTITUTED: () => (
    <SwapHorizAlert sx={{ fontSize: "2.5vw" }} />
  ),
};

const TeamIneligible: FC<TeamIneligibleProps> = ({ persons }) => {
  return (
    <TableContainer>
      <Table size="small" sx={{ tableLayout: "fixed" }}>
        <TableHead>
          <TableRow>
            <CenteredHeaderTableCell
              style={{
                lineHeight: 0,
                width: "5vw",
              }}
            ></CenteredHeaderTableCell>
            <CenteredHeaderTableCell
              style={{
                lineHeight: 0,
              }}
            >
              <Person
                sx={{
                  verticalAlign: "middle",
                  fontSize: "clamp(15px, 4vw, 24px)",
                }}
              />
            </CenteredHeaderTableCell>
          </TableRow>
        </TableHead>
        <TableBody sx={{ borderCollapse: "collapse" }}>
          {persons.map((person, i) => {
            return (
              <TableRow key={`sanctions-${i}`}>
                <CenteredTableCell padding={"none"}>
                  {IneligibleSymbolMap[
                    person.admissionStatus as Exclude<
                      AdmissionStatus,
                      "ADMITTED"
                    >
                  ]()}
                </CenteredTableCell>
                <CenteredTableCell padding={"none"}>
                  <Box
                    display={"flex"}
                    alignItems={"center"}
                    justifyContent={"flex-start"}
                    fontSize={"2.5vw"}
                    gap={"2vw"}
                  >
                    <Typography
                      fontSize={"2.5vw"}
                      overflow={"hidden"}
                      whiteSpace={"nowrap"}
                      textOverflow={"ellipsis"}
                    >
                      {getAddressee(person)}
                    </Typography>
                  </Box>
                </CenteredTableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const LeftTeamIneligible: FC<Omit<TeamIneligibleProps, "side">> = (props) =>
  TeamIneligible({ side: "left", ...props });
const RightTeamIneligible: FC<Omit<TeamIneligibleProps, "side">> = (props) =>
  TeamIneligible({ side: "right", ...props });

const getTeamIneligiblePersons = (teamSquad: TeamSquad) =>
  [...teamSquad.players, ...teamSquad.officials].filter(
    (p) => p.admissionStatus !== "ADMITTED",
  );

const RefereeTabletIneligibleList: FC = () => {
  const { leftTeam, rightTeam } = useTeamSides(useAppDataContext());
  const teamSquads = gameState("teamSquads");

  const leftTeamSquad = teamSquads[leftTeam];
  const rightTeamSquad = teamSquads[rightTeam];

  const leftIneligiblePersons = getTeamIneligiblePersons(leftTeamSquad);
  const rightIneligiblePersons = getTeamIneligiblePersons(rightTeamSquad);

  return leftIneligiblePersons.length > 0 ||
    rightIneligiblePersons.length > 0 ? (
    <>
      <Box>
        <Typography textAlign={"center"} variant={"h6"}>
          Ausgeschieden:
        </Typography>
        <Box
          display={"grid"}
          gap={1}
          gridTemplateColumns={"repeat(2, minmax(0, 1fr))"}
        >
          <LeftTeamIneligible persons={leftIneligiblePersons} />
          <RightTeamIneligible persons={rightIneligiblePersons} />
        </Box>
      </Box>
      <PaddedDivider />
    </>
  ) : null;
};

export default RefereeTabletIneligibleList;
